import { memo } from 'react';
import { CornerUpLeft, Forward } from 'lucide-react';
import { cn } from '@cyphex/ui';
import type { Message, MessagePreview } from '@cyphex/types';

interface QuotedMessageProps {
  preview: MessagePreview;
  authorName: string;
  onJump?: () => void;
  className?: string;
}

/** The quoted strip above a reply. Clicking it jumps to the original. */
export const QuotedMessage = memo(function QuotedMessage({
  preview,
  authorName,
  onJump,
  className,
}: QuotedMessageProps) {
  return (
    <button
      type="button"
      onClick={onJump}
      className={cn(
        'flex w-full max-w-bubble items-start gap-2 rounded-md border-l-2 border-primary bg-primary-subtle/60 px-2.5 py-1.5 text-left transition-colors duration-fast hover:bg-primary-subtle',
        className,
      )}
    >
      <CornerUpLeft size={14} strokeWidth={2.5} className="mt-0.5 shrink-0 text-primary" aria-hidden />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-primary">{authorName}</span>
        <span className="line-clamp-2 block text-sm text-muted-foreground">{preview.text}</span>
      </span>
    </button>
  );
});

interface ForwardBannerProps {
  from: NonNullable<Message['forwardedFrom']>;
}

/** "Forwarded from …" line above a forwarded message. */
export const ForwardBanner = memo(function ForwardBanner({ from }: ForwardBannerProps) {
  return (
    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Forward size={14} strokeWidth={2.5} aria-hidden />
      Forwarded from {from.conversationTitle ?? from.sender?.displayName ?? 'another chat'}
    </p>
  );
});
